import * as React from 'react';
import { Modal } from '@ixicare/ui';
import { IndoorGeofencingMode, ZoneEntry } from '@ixicare/resources';
import { t } from '~/i18n';
import GeofenceCreatorContainer from '~modules/geofencing/container/GeofenceCreatorContainer';
import type { GeofenceSelection } from '~modules/zone-presets/useZonePresetSelection';
import type { useZonePresets } from '~modules/zone-presets/useZonePresets';
import modalStyles from '~modules/geofencing/container/geofenceEditorModal.module.css';
import { InputContainer, InputLabel, Value } from './inputStyles';

interface Props {
  siteUuid: string;
  siteId?: number;
  allZones: ZoneEntry[];
  mode: IndoorGeofencingMode;
  selectedZones: string[];
  zonePresetId: string | null;
  /** The site's presets, so this can name the one being followed. Fetched by the page. */
  presets?: NonNullable<ReturnType<typeof useZonePresets>['data']>;
  onChange: (selection: GeofenceSelection) => void;
}

export default function ZonesInput({
  siteUuid,
  siteId,
  allZones,
  mode,
  selectedZones,
  zonePresetId,
  presets,
  onChange,
}: Props) {
  const mappedZones = React.useMemo(
    () => selectedZones.map((uuid) => allZones.find((z) => z.uuid === uuid)).filter((z): z is ZoneEntry => !!z),
    [selectedZones, allZones]
  );
  const followed = presets?.find((preset) => preset.id === zonePresetId);
  const [isEdit, setEdit] = React.useState(false);

  // Naming the preset rather than listing its zones is the point of following one: it is what tells
  // the carer this sender moves with the department instead of holding a list of its own.
  const summary = followed
    ? t('geofence.preset.following', { name: followed.name })
    : mappedZones.length
      ? mappedZones.map((z) => z.name).join(', ')
      : t('profile-configuration.geofence.indoor.zonesEmpty');

  return (
    <InputContainer>
      <InputLabel>{t(`profile-configuration.geofence.indoor.${mode}.zones`)}</InputLabel>
      <Value id="geofence-zones" onClick={() => setEdit(true)}>
        {summary}
      </Value>

      {isEdit && (
        <Modal onClose={() => setEdit(false)} className={modalStyles.wide}>
          <GeofenceCreatorContainer
            subject="alarmSender"
            mode={mode}
            selectedZones={selectedZones}
            zonePresetId={zonePresetId}
            onSave={(selection) => {
              setEdit(false);
              onChange(selection);
            }}
            onCancel={() => setEdit(false)}
            siteUuid={siteUuid}
            siteId={siteId}
          />
        </Modal>
      )}
    </InputContainer>
  );
}
